import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import type { Database } from '../lib/supabase'
import { decryptWithFamilyKey } from '../utils/keyManagement'
// import { encryptWithFamilyKey } from '../utils/keyManagement' - usunięte bo nie używane
// import { getOrPromptForPassword } from '../utils/passwordHandling' - usunięte bo nie używane
import { getPassword } from '../utils/passwordHandling'
import { logger } from '../utils/logger'
import { pickPrimaryFamilyId } from '../utils/familySelection'

type Family = Database['public']['Tables']['families']['Row']
type FamilyMember = Database['public']['Tables']['family_members']['Row'] & {
  user_email?: string; // Dodatkowe pole z email użytkownika
}
type Child = Database['public']['Tables']['children']['Row']

interface FamilyState {
  family: Family | null
  members: FamilyMember[]
  children: Child[]
  isLoading: boolean
  error: string | null
  initialized: boolean
  
  // Akcje
  initialize: () => Promise<void>
  createFamily: (name: string) => Promise<string | null>
  updateFamily: (data: { name?: string, home_a_name?: string, home_b_name?: string }) => Promise<boolean>
  updateAssignedHome: (home: 'home_a' | 'home_b') => Promise<boolean>
  addChild: (name: string) => Promise<string | null>
  updateChild: (childId: string, data: { name?: string }) => Promise<boolean>
  removeChild: (childId: string) => Promise<boolean>
  fetchFamily: (familyId: string) => Promise<void>
  fetchChildren: (familyId: string) => Promise<void>
  fetchFamilyMembers: (familyId: string) => Promise<void>
  getUserFamilyId: () => Promise<string | null>
  hasFamily: () => boolean
  clearCache: () => void
}

// In-memory memoization for familyId per user and in-flight deduplication
const familyIdMemoryCache: Record<string, { value: string | null; ts: number }> = {}
let inFlightFamilyIdPromise: Promise<string | null> | null = null

// Rodzina użytkownika. Przy kilku członkostwach (rejestracja przed zaproszeniem)
// wybór deterministyczny zamiast `.single()`, które dawało wtedy PGRST116 i pustą aplikację.
async function fetchPrimaryFamilyId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('family_members')
    .select('family_id, joined_at')
    .eq('user_id', userId)
  if (error) throw error

  const rows = data ?? []
  if (rows.length <= 1) return rows[0]?.family_id ?? null

  const ids = rows.map(r => r.family_id)
  const [members, kids] = await Promise.all([
    supabase.from('family_members').select('family_id, user_id').in('family_id', ids),
    supabase.from('children').select('family_id').in('family_id', ids),
  ])
  return pickPrimaryFamilyId(rows.map(r => ({
    family_id: r.family_id,
    joined_at: r.joined_at,
    otherMembers: (members.data ?? []).filter(x => x.family_id === r.family_id && x.user_id !== userId).length,
    children: (kids.data ?? []).filter(x => x.family_id === r.family_id).length,
  })))
}

export const useFamilyStore = create<FamilyState>()(
  persist(
    (set, get) => ({
      family: null,
      members: [],
      children: [],
      isLoading: false,
      error: null,
      initialized: false,
      
      initialize: async () => {
        const { user } = useAuthStore.getState()
        if (!user) {
          logger.debug('🏠 FamilyStore: Brak użytkownika, pomijam inicjalizację')
          set({ isLoading: false, initialized: true })
          return
        }
        
        // Sprawdź czy już zainicjalizowano
        const { initialized, isLoading } = get()
        if (initialized) {
          logger.debug('🏠 FamilyStore: Już zainicjalizowano, pomijam')
          return
        }
        
        // Sprawdź czy już trwa inicjalizacja
        if (isLoading) {
          logger.debug('🏠 FamilyStore: Inicjalizacja już trwa, pomijam')
          return
        }
        
        logger.debug('🏠 Inicjalizacja familyStore...')
        set({ isLoading: true, error: null })
        
        try {
          // KROK 1: Sprawdź czy użytkownik już należy do rodziny
          // Użyj bezpośrednio zapytania do bazy zamiast getUserFamilyId() żeby uniknąć pętli
          let familyId: string | null = null
          
          // Sprawdź cache najpierw
          const cacheKey = `user-family-${user.id}`
          const cached = sessionStorage.getItem(cacheKey)
          if (cached) {
            try {
              const parsed = JSON.parse(cached)
              const nowTs = Date.now()
              if (nowTs - parsed.timestamp < 10 * 60 * 1000) {
                familyId = parsed.familyId
                logger.debug(`📦 Używam cache dla ID rodziny użytkownika`)
              }
            } catch {}
          }
          
          // Jeśli nie ma w cache, pobierz z bazy
          if (!familyId) {
            familyId = await fetchPrimaryFamilyId(user.id).catch(() => null)

            if (familyId) {
              // Zapisz w cache
              sessionStorage.setItem(cacheKey, JSON.stringify({ 
                familyId, 
                timestamp: Date.now() 
              }))
            }
          }
          
          if (familyId) {
            logger.debug(`👨‍👩‍👧‍👦 Użytkownik należy do rodziny: ${familyId}`)
            // Wykonuj zapytania równolegle, aby przyspieszyć ładowanie
            await Promise.allSettled([
              get().fetchFamily(familyId),
              get().fetchChildren(familyId),
              get().fetchFamilyMembers(familyId)
            ]);
          } else {
            logger.debug('🔍 Użytkownik nie należy do żadnej rodziny')
            
            // KROK 2: Zaproszenia są teraz automatycznie akceptowane przez trigger bazodanowy
            // po potwierdzeniu email, więc nie ma potrzeby sprawdzania localStorage
          }
          
          set({ isLoading: false, initialized: true })
          logger.debug('✅ FamilyStore inicjalizacja zakończona pomyślnie')
        } catch (error: any) {
          console.error('❌ Błąd inicjalizacji familyStore:', error)
          set({ 
            error: error.message || 'Błąd inicjalizacji familyStore',
            isLoading: false,
            initialized: true
          })
          logger.debug('⚠️ FamilyStore inicjalizacja zakończona z błędem, ale initialized: true')
        }
      },
      
      createFamily: async (name: string) => {
        const { user } = useAuthStore.getState()
        if (!user) {
          logger.debug('🚫 Brak użytkownika, nie można utworzyć rodziny')
          set({ error: 'Musisz być zalogowany, aby utworzyć rodzinę', isLoading: false })
          return null
        }

        // Guard: użytkownik nie może mieć dwóch rodzin
        if (get().family) {
          set({ error: 'Masz już rodzinę', isLoading: false })
          return null
        }

        // Walidacja nazwy rodziny
        const trimmed = name.trim()
        if (!trimmed || trimmed.length < 2 || trimmed.length > 100) {
          set({ error: 'Nazwa rodziny musi mieć od 2 do 100 znaków', isLoading: false })
          return null
        }

        logger.debug('🏠 Tworzenie nowej rodziny:', trimmed)
        set({ isLoading: true, error: null })

        try {
          // Tworzenie nowej rodziny
          const { data: familyData, error: familyError } = await supabase
            .from('families')
            .insert({
              name: trimmed,
              home_a_name: 'Dom A',
              home_b_name: 'Dom B'
            })
            .select()
          
          if (familyError) throw familyError
          
          if (!familyData || familyData.length === 0) {
            throw new Error('Nie udało się utworzyć rodziny')
          }
          
          const family = familyData[0] as Family
          logger.debug(`✅ Rodzina utworzona: ${family.id}`)
          
          // Dodanie użytkownika jako głównego rodzica (parent_a)
          const { error: memberError } = await supabase
            .from('family_members')
            .insert({
              family_id: family.id,
              user_id: user.id,
              role: 'parent_a',
              encryption_key: '', // Tymczasowo pusty
              invited_at: new Date().toISOString(),
              joined_at: new Date().toISOString()
            })
          
          if (memberError) throw memberError
          
          logger.debug(`✅ Użytkownik dodany jako główny rodzic`)
          
          // Pobierz dane rodziny
          await get().fetchFamily(family.id)
          
          set({ isLoading: false })
          return family.id
        } catch (error: any) {
          console.error('❌ Błąd tworzenia rodziny:', error)
          set({ 
            error: error.message || 'Błąd tworzenia rodziny',
            isLoading: false
          })
          return null
        }
      },
      
      addChild: async (name) => {
        const { family } = get()
        if (!family) return null
        
        logger.debug(`👶 Dodawanie dziecka do rodziny ${family.id}...`)
        set({ isLoading: true, error: null })
        
        try {
          // Prosty insert bez obsługi birth_date
          const processedName = name;

          const { data, error } = await supabase
            .from('children')
            .insert({
              family_id: family.id,
              name: processedName
            })
            .select()
          
          if (error) throw error
          
          if (!data || data.length === 0) {
            throw new Error('Nie udało się dodać dziecka')
          }
          
          const child = data[0] as Child
          logger.debug(`✅ Dziecko dodane: ${child.id}`)
          
          // Wyczyść cache przed pobraniem świeżych danych
          get().clearCache()
          
          // Wyczyść również cache kalendarza
          const { useCalendarStore } = await import('./calendarStore')
          useCalendarStore.getState().clearCache()
          
          // Aktualizuj listę dzieci
          await get().fetchChildren(family.id)
          
          set({ isLoading: false })
          return child.id
        } catch (error: any) {
          console.error('❌ Błąd dodawania dziecka:', error)
          set({ 
            error: error.message || 'Błąd dodawania dziecka',
            isLoading: false
          })
          return null
        }
      },
      
      // inviteFamilyMember usuniete 2026-09-16 (audyt wektorow).
      // Zapraszanie idzie wylacznie przez POST /api/invitations/create
      // (FamilyMembersList.tsx) - tam sa limity: cooldown po odrzuceniu, limit
      // aktywnych zaproszen i dlawik wysylki maili. Ta wersja pisala wprost do
      // family_invitations i family_members, wiec te limity omijala, a do
      // wyszukania uzytkownika wolala rpc('get_user_by_email'), ktorej w bazie
      // w ogole nie ma. Zadnego wywolania w aplikacji juz nie mialo.
      
      fetchFamily: async (familyId) => {
        logger.debug(`🏠 Pobieranie danych rodziny ${familyId}...`)
        
        // Sprawdź cache
        const cacheKey = `family-data-${familyId}`
        const cached = sessionStorage.getItem(cacheKey)
        if (cached) {
          try {
            const parsed = JSON.parse(cached)
            // Cache ważny przez 10 minut
            if (Date.now() - parsed.timestamp < 10 * 60 * 1000) {
              logger.debug('📦 Używam cache dla danych rodziny')
              set({ 
                family: parsed.family,
                isLoading: false
              })
              return
            }
          } catch (e) {
            // Ignoruj błędy parsowania cache
          }
        }
        
        set({ isLoading: true, error: null })
        
        try {
          const queryPromise = supabase
            .from('families')
            .select('*')
            .eq('id', familyId)
            .single()
          
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Query timeout')), 10000)
          )
          
          const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any
          
          if (error) throw error
          
          logger.debug(`✅ Pobrano dane rodziny`)
          
          const family = data as Family
          
          // Cache wynik
          sessionStorage.setItem(cacheKey, JSON.stringify({
            family,
            timestamp: Date.now()
          }))
          
          set({ 
            family,
            isLoading: false
          })
        } catch (error: any) {
          console.error('❌ Błąd pobierania danych rodziny:', error)
          set({ 
            error: error.message || 'Błąd pobierania danych rodziny',
            isLoading: false
          })
        }
      },
      
      fetchChildren: async (familyId) => {
        logger.debug(`👶 Pobieranie dzieci dla rodziny ${familyId}...`)
        
        // Sprawdź cache
        const cacheKey = `family-children-${familyId}`
        const cached = sessionStorage.getItem(cacheKey)
        if (cached) {
          try {
            const parsed = JSON.parse(cached)
            // Cache ważny przez 10 minut
            if (Date.now() - parsed.timestamp < 10 * 60 * 1000) {
              logger.debug('📦 Używam cache dla dzieci rodziny')
              set({ 
                children: parsed.children,
                isLoading: false
              })
              return
            }
          } catch (e) {
            // Ignoruj błędy parsowania cache
          }
        }
        
        set({ isLoading: true, error: null })
        
        try {
          const { data, error } = await supabase
            .from('children')
            .select('*')
            .eq('family_id', familyId)
            .order('created_at')
          
          if (error) throw error
          
          logger.debug(`✅ Pobrano ${data?.length || 0} dzieci`)
          
          // Deszyfrowanie danych dzieci (tylko imię; birth_date wycofane)
          const processedChildren = await Promise.all((data || []).map(async (child: any) => {
            let processedChild = { ...child };
            
            // Próbujemy odszyfrować imię, jeśli wygląda na zaszyfrowane (JSON)
            if (child.name && typeof child.name === 'string' && child.name.startsWith('{') && child.name.endsWith('}')) {
              try {
                logger.debug(`🔓 Próba deszyfrowania imienia dla dziecka ${child.id}...`);
                
                // Pobierz aktualnego użytkownika
                const { user } = useAuthStore.getState();
                if (!user) {
                  throw new Error('Brak zalogowanego użytkownika');
                }
                
                // Pobierz hasło E2EE z sesji użytkownika
                const password = await getPassword(user.id);
                if (!password) {
                  logger.warn(`⚠️ Hasło E2EE niedostępne dla użytkownika ${user.id} — pomijam deszyfrowanie imienia dziecka ${child.id}`);
                  return processedChild;
                }
                
                try {
                  // Deszyfrowanie imienia
                  const encryptedName = JSON.parse(child.name);
                  const decryptedName = await decryptWithFamilyKey(encryptedName, familyId, password);
                  
                  if (decryptedName) {
                    processedChild.name = decryptedName;
                    logger.debug('✅ Imię dziecka odszyfrowane');
                  } else {
                    // Jeśli deszyfrowanie zwróciło null, użyj nazwy zastępczej
                    processedChild.name = `Dziecko ${child.id.substring(0, 4)}`;
                    logger.debug('⚠️ Nie udało się odszyfrować imienia, używam nazwy zastępczej');
                  }
                } catch (parseError) {
                  // Jeśli dane są nieprawidłowym JSON, użyj nazwy zastępczej
                  processedChild.name = `Dziecko ${child.id.substring(0, 4)}`;
                  console.error(`❌ Błąd parsowania zaszyfrowanej nazwy:`, parseError);
                }
              } catch (decryptError) {
                console.error(`❌ Błąd deszyfrowania imienia dla dziecka ${child.id}:`, decryptError);
                // Użyj nazwy zastępczej zamiast zaszyfrowanego ciągu
                processedChild.name = `Dziecko ${child.id.substring(0, 4)}`;
              }
            }
            
            // birth_date wycofane – nie deszyfrujemy
            
            return processedChild;
          }));
          
          const children = processedChildren as Child[];
          
          // Cache wynik
          sessionStorage.setItem(cacheKey, JSON.stringify({
            children,
            timestamp: Date.now()
          }));
          
          set({ 
            children,
            isLoading: false
          })
        } catch (error: any) {
          console.error('❌ Błąd pobierania dzieci:', error)
          set({ 
            error: error.message || 'Błąd pobierania dzieci',
            isLoading: false
          })
        }
      },
      
      fetchFamilyMembers: async (familyId) => {
        logger.debug(`👨‍👩‍👧‍👦 Pobieranie członków rodziny ${familyId}...`)
        
        // Sprawdź cache
        const cacheKey = `family-members-${familyId}`
        const cached = sessionStorage.getItem(cacheKey)
        if (cached) {
          try {
            const parsed = JSON.parse(cached)
            // Cache ważny przez 10 minut
            if (Date.now() - parsed.timestamp < 10 * 60 * 1000) {
              console.log('📦 Używam cache dla członków rodziny')
              set({ 
                members: parsed.members,
                isLoading: false
              })
              return
            }
          } catch (e) {
            // Ignoruj błędy parsowania cache
          }
        }
        
        set({ isLoading: true, error: null })
        
        try {
          logger.debug('🚀 Próba pobrania członków przez RPC get_family_members_detailed...');
          
          // Użyj RPC function która zwraca też email użytkownika
          let { data, error } = await supabase.rpc('get_family_members_detailed', {
            p_family_id: familyId
          });
          
          // Jeśli główna funkcja nie działa, spróbuj uproszczonej wersji
          if (error) {
            logger.debug('⚠️ Główna funkcja RPC nie działa, próbuję uproszczonej wersji:', error);
            
            const fallback = await supabase.rpc('get_family_members_simple', {
              p_family_id: familyId
            });
            
            if (fallback.error) {
              logger.debug('⚠️ Uproszczona funkcja też nie działa, próbuję bezpośredniego zapytania:', fallback.error);
              
              // Ostatni fallback - bezpośrednie zapytanie
              const direct = await supabase
                .from('family_members')
                .select(`
                  id,
                  user_id,
                  role,
                  joined_at,
                  invited_at,
                  assigned_home
                `)
                .eq('family_id', familyId);
                
              if (direct.error) {
                throw direct.error;
              }
              
              // Mapuj wyniki bez emaili
              data = (direct.data || []).map((member: any) => ({
                member_id: member.id,
                user_id: member.user_id,
                user_email: `user-${member.user_id.slice(0, 8)}@example.com`, // Placeholder email
                role: member.role,
                joined_at: member.joined_at,
                invited_at: member.invited_at,
                assigned_home: member.assigned_home
              }));
              
              logger.debug('✅ Bezpośrednie zapytanie działa, użyto placeholder emaili');
            } else {
              data = fallback.data;
              logger.debug('✅ Uproszczona funkcja RPC działa');
            }
          } else {
            logger.debug('✅ Główna funkcja RPC działa poprawnie');
          }
          
          logger.debug(`✅ Pobrano ${data?.length || 0} członków rodziny`)
          
          // Mapuj wyniki RPC na format FamilyMember z dodatkowym email
          const members = (data || []).map((member: any) => {
            logger.debug(`🔍 DEBUG - member z bazy danych:`, member);
            logger.debug(`🔍 DEBUG - rola członka: "${member.role}"`);

            return {
              id: member.member_id,
              user_id: member.user_id,
              family_id: familyId,
              role: member.role,
              joined_at: member.joined_at,
              invited_at: member.invited_at,
              user_email: member.user_email, // Dodatkowe pole z email
              encryption_key: '', // Domyślne puste
              assigned_home: member.assigned_home || null
            };
          });

          // RPC może nie zwracać assigned_home — dobierz z tabeli bezpośrednio
          const needsHome = members.some((m: any) => m.assigned_home === null)
          if (needsHome) {
            try {
              const { data: homeData } = await supabase
                .from('family_members')
                .select('user_id, assigned_home')
                .eq('family_id', familyId)
              if (homeData) {
                const homeMap = new Map(homeData.map((h: any) => [h.user_id, h.assigned_home]))
                for (const m of members) {
                  if (m.assigned_home === null && homeMap.get(m.user_id)) {
                    (m as any).assigned_home = homeMap.get(m.user_id)
                  }
                }
              }
            } catch {
              // Kontynuuj bez assigned_home — fallback na rolę zadziała
            }
          }
          
          // Cache wynik
          sessionStorage.setItem(cacheKey, JSON.stringify({
            members,
            timestamp: Date.now()
          }));
          
          set({ 
            members,
            isLoading: false
          })
        } catch (error: any) {
          console.error('❌ Błąd pobierania członków rodziny:', error)
          set({ 
            error: error.message || 'Błąd pobierania członków rodziny',
            isLoading: false
          })
        }
      },
      
      getUserFamilyId: async () => {
        const { user } = useAuthStore.getState()
        if (!user) return null
        
        // 1) Jeżeli już mamy rodzinę w stanie – użyj od razu
        const existingFamily = get().family
        if (existingFamily?.id) {
          return existingFamily.id
        }
        
        // 2) In-memory cache (5 minut)
        const mem = familyIdMemoryCache[user.id]
        const nowTs = Date.now()
        if (mem && (nowTs - mem.ts < 5 * 60 * 1000)) {
          return mem.value
        }
        
        // 3) Dedup in-flight
        if (inFlightFamilyIdPromise) {
          return inFlightFamilyIdPromise
        }
        
        // 4) Sprawdź sessionStorage cache (10 minut)
        const cacheKey = `user-family-${user.id}`
        const cached = sessionStorage.getItem(cacheKey)
        if (cached) {
          try {
            const parsed = JSON.parse(cached)
            if (nowTs - parsed.timestamp < 10 * 60 * 1000) {
              familyIdMemoryCache[user.id] = { value: parsed.familyId, ts: nowTs }
              return parsed.familyId
            }
          } catch {}
        }
        
        // 5) Właściwe pobranie z Supabase (bez sztucznego timeoutu)
        inFlightFamilyIdPromise = (async () => {
          try {
            let familyId: string | null = null
            try {
              familyId = await fetchPrimaryFamilyId(user.id)
            } catch {
              // fallback RPC
              try {
                const rpc = await supabase.rpc('get_user_family_id', { p_user_id: user.id })
                familyId = (rpc.data as string | null) || null
              } catch {}
            }

            sessionStorage.setItem(cacheKey, JSON.stringify({ familyId, timestamp: nowTs }))
            familyIdMemoryCache[user.id] = { value: familyId, ts: nowTs }
            return familyId
          } catch (e) {
            return null
          } finally {
            inFlightFamilyIdPromise = null
          }
        })()
        
        return inFlightFamilyIdPromise
      },
      
      hasFamily: () => {
        return get().family !== null
      },
      
      updateFamily: async (data) => {
        const { family } = get()
        if (!family) return false
        
        logger.debug('🏠 Aktualizacja danych rodziny...')
        set({ isLoading: true, error: null })
        
        try {
          const { error } = await supabase
            .from('families')
            .update(data)
            .eq('id', family.id)
          
          if (error) throw error
          
          logger.debug('✅ Dane rodziny zaktualizowane')
          
          // Wyczyść cache dla tej rodziny
          const cacheKey = `family-data-${family.id}`
          sessionStorage.removeItem(cacheKey)
          
          // Odśwież dane rodziny
          await get().fetchFamily(family.id)
          
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('❌ Błąd aktualizacji danych rodziny:', error)
          set({ 
            error: error.message || 'Błąd aktualizacji danych rodziny',
            isLoading: false
          })
          return false
        }
      },
      
      updateAssignedHome: async (home) => {
        const { user } = useAuthStore.getState()
        const { family } = get()
        if (!user || !family) return false

        logger.debug('🏠 Aktualizacja assigned_home:', home)
        try {
          // Ustaw assigned_home dla bieżącego użytkownika
          const { error } = await supabase
            .from('family_members')
            .update({ assigned_home: home } as any)
            .eq('family_id', family.id)
            .eq('user_id', user.id)

          if (error) throw error

          // Drugi rodzic dostaje przeciwny dom
          const otherHome = home === 'home_a' ? 'home_b' : 'home_a'
          const otherParent = get().members.find(
            m => m.user_id !== user.id && (m.role === 'parent_a' || m.role === 'parent_b')
          )
          if (otherParent) {
            await supabase
              .from('family_members')
              .update({ assigned_home: otherHome } as any)
              .eq('family_id', family.id)
              .eq('user_id', otherParent.user_id)
          }

          // Wyczyść cache i odśwież members
          sessionStorage.removeItem(`family-members-${family.id}`)
          await get().fetchFamilyMembers(family.id)
          logger.debug('✅ assigned_home zaktualizowane')
          return true
        } catch (error: any) {
          console.error('❌ Błąd aktualizacji assigned_home:', error)
          return false
        }
      },

      updateChild: async (childId, data) => {
        const { family } = get()
        if (!family) return false
        
        logger.debug(`👶 Aktualizacja dziecka ${childId}...`)
        set({ isLoading: true, error: null })
        
        try {
          // Przygotuj dane z właściwą walidacją
          const updateData: any = {}
          
          // Dodaj imię jeśli jest podane
          if (data.name !== undefined && data.name !== null) {
            updateData.name = data.name.trim()
          }
          
          // Ignorujemy birth_date – pole wycofane z UI
          
          // Jeśli nie ma żadnych danych do aktualizacji
          if (Object.keys(updateData).length === 0) {
            logger.debug('🔄 Brak danych do aktualizacji')
            set({ isLoading: false })
            return true
          }
          
          logger.debug('🔄 Dane do aktualizacji:', updateData)
          
          // Aktualizacja dziecka w bazie danych
          const { error } = await supabase
            .from('children')
            .update(updateData)
            .eq('id', childId)
            .eq('family_id', family.id)
          
          if (error) throw error
          
          logger.debug(`✅ Dziecko zaktualizowane: ${childId}`)
          
          // Wyczyść cache przed pobraniem świeżych danych
          get().clearCache()
          
          // Wyczyść również cache kalendarza
          const { useCalendarStore } = await import('./calendarStore')
          useCalendarStore.getState().clearCache()
          
          // Aktualizuj listę dzieci
          await get().fetchChildren(family.id)
          
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('❌ Błąd aktualizacji dziecka:', error)
          set({ 
            error: error.message || 'Błąd aktualizacji dziecka',
            isLoading: false
          })
          return false
        }
      },
      
      removeChild: async (childId) => {
        const { family } = get()
        if (!family) return false
        
        logger.debug(`👶 Usuwanie dziecka ${childId} wraz z wszystkimi wpisami kalendarza...`)
        set({ isLoading: true, error: null })
        
        try {
          // Dzięki CASCADE w bazie danych, usunięcie dziecka automatycznie
          // usunie wszystkie powiązane wpisy w calendar_entries
          const { error } = await supabase
            .from('children')
            .delete()
            .eq('id', childId)
            .eq('family_id', family.id)
          
          if (error) throw error
          
          logger.debug(`✅ Dziecko i wszystkie powiązane wpisy kalendarza usunięte: ${childId}`)
          
          // Wyczyść cache przed pobraniem świeżych danych
          get().clearCache()
          
          // Wyczyść również cache kalendarza
          const { useCalendarStore } = await import('./calendarStore')
          useCalendarStore.getState().clearCache()
          
          // Aktualizuj listę dzieci
          await get().fetchChildren(family.id)
          
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('❌ Błąd usuwania dziecka:', error)
          set({ 
            error: error.message || 'Błąd usuwania dziecka',
            isLoading: false
          })
          return false
        }
      },
      
      // Funkcja do czyszczenia cache'u
      clearCache: () => {
        const { user } = useAuthStore.getState()
        if (user) {
          // Wyczyść cache ID rodziny użytkownika
          const userFamilyKey = `user-family-${user.id}`
          sessionStorage.removeItem(userFamilyKey)
          
          // Pobierz ID rodziny z store'a
          const { family } = get()
          if (family) {
            // Wyczyść cache danych rodziny
            const familyDataKey = `family-data-${family.id}`
            sessionStorage.removeItem(familyDataKey)
            
            // Wyczyść cache dzieci rodziny
            const familyChildrenKey = `family-children-${family.id}`
            sessionStorage.removeItem(familyChildrenKey)
            
            // Wyczyść cache członków rodziny
            const familyMembersKey = `family-members-${family.id}`
            sessionStorage.removeItem(familyMembersKey)
          }
          
          logger.debug('🗑️ Wyczyszczono cache danych rodziny')
        }
      }
    }),
    {
      name: '2domy-family-storage',
      partialize: () => ({
        // NIE persistuj initialized - to musi być świeże przy każdym zalogowaniu
        // Pusta persistencja - nic nie zapisujemy
      }),
    }
  )
) 